import React, { useEffect, useState } from "react";
import { api, apiErr } from "../../api/client";
import { ADMIN } from "../../constants/testIds";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { toast } from "sonner";
import { Building2, Clock, Tag, Save, Plus, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const DEFAULT_HOURS = { start: "07:00", end: "17:00", days: ["mon", "tue", "wed", "thu", "fri"] };
const WEEKDAYS = [
    { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" }, { key: "wed", label: "Wed" },
    { key: "thu", label: "Thu" }, { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" }, { key: "sun", label: "Sun" },
];

export default function Settings() {
    const { user } = useAuth();
    const [form, setForm] = useState(null);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [newJobType, setNewJobType] = useState("");

    const isAdmin = user?.role === "admin";

    useEffect(() => {
        api.get("/settings").then((r) => {
            const d = r.data;
            setForm({
                business_name: d.business_name || "",
                abn: d.abn || "",
                address: d.address || "",
                phone: d.phone || "",
                email: d.email || "",
                website: d.website || "",
                job_types: d.job_types || [],
                working_hours: d.working_hours || DEFAULT_HOURS,
            });
        }).catch((e) => toast.error(apiErr(e)));
    }, []);

    if (!form) return <div className="p-8 text-muted">Loading…</div>;

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/settings", {
                ...form,
                reason: reason || undefined,
            });
            toast.success("Settings saved");
            setReason("");
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };

    const toggleDay = (k) => {
        const cur = new Set(form.working_hours.days || []);
        if (cur.has(k)) cur.delete(k); else cur.add(k);
        setForm({ ...form, working_hours: { ...form.working_hours, days: WEEKDAYS.map((w) => w.key).filter((w) => cur.has(w)) } });
    };

    const addJobType = () => {
        const t = newJobType.trim();
        if (!t) return;
        if (form.job_types.includes(t)) { toast.message("Already exists"); return; }
        setForm({ ...form, job_types: [...form.job_types, t] });
        setNewJobType("");
    };
    const removeJobType = (t) => {
        setForm({ ...form, job_types: form.job_types.filter((x) => x !== t) });
    };

    return (
        <div data-testid={ADMIN.settingsRoot} className="space-y-6 max-w-4xl">
            <div>
                <div className="eyebrow">Admin</div>
                <h1 className="text-4xl font-extrabold tracking-[-0.03em] leading-none">Settings</h1>
                <p className="text-muted mt-2 text-sm">Business info, working hours, job types. Everything below is audit-logged.</p>
            </div>

            {!isAdmin && (
                <div className="km-card p-4 border-l-4" style={{ borderLeftColor: "var(--km-yellow)", background: "var(--km-yellow-l)" }}>
                    <div className="text-sm text-[color:var(--km-yellow-d)]">Read-only — admin role required to edit.</div>
                </div>
            )}

            {/* Business info */}
            <section className="km-card p-6 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Building2 size={18} className="text-accent" />
                    <h2 className="text-xl font-extrabold tracking-[-0.03em]">Business info</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Business name</Label>
                        <Input disabled={!isAdmin} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                    </div>
                    <div>
                        <Label>ABN</Label>
                        <Input disabled={!isAdmin} className="num" value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Address</Label>
                        <Input disabled={!isAdmin} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                    </div>
                    <div>
                        <Label>Phone</Label>
                        <Input disabled={!isAdmin} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <Input disabled={!isAdmin} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Website</Label>
                        <Input disabled={!isAdmin} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                    </div>
                </div>
            </section>

            {/* Working hours */}
            <section className="km-card p-6 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Clock size={18} className="text-forest" />
                    <h2 className="text-xl font-extrabold tracking-[-0.03em]">Working hours</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Start</Label>
                        <Input disabled={!isAdmin} type="time" className="num" value={form.working_hours.start || ""} onChange={(e) => setForm({ ...form, working_hours: { ...form.working_hours, start: e.target.value } })} />
                    </div>
                    <div>
                        <Label>End</Label>
                        <Input disabled={!isAdmin} type="time" className="num" value={form.working_hours.end || ""} onChange={(e) => setForm({ ...form, working_hours: { ...form.working_hours, end: e.target.value } })} />
                    </div>
                </div>
                <div>
                    <Label>Working days</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {WEEKDAYS.map((w) => {
                            const active = (form.working_hours.days || []).includes(w.key);
                            return (
                                <button
                                    key={w.key}
                                    disabled={!isAdmin}
                                    onClick={() => toggleDay(w.key)}
                                    className={"km-chip cursor-pointer transition " + (active ? "km-chip-navy-solid" : "km-chip-grey")}
                                >
                                    {w.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Job types */}
            <section className="km-card p-6 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <Tag size={18} className="text-[color:var(--km-yellow-d)]" />
                    <h2 className="text-xl font-extrabold tracking-[-0.03em]">Job types</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                    {form.job_types.map((t) => (
                        <span key={t} className="km-chip km-chip-navy inline-flex items-center gap-2 pr-1.5">
                            {t}
                            {isAdmin && (
                                <button onClick={() => removeJobType(t)} className="hover:text-[color:var(--km-red)]"><X size={11} /></button>
                            )}
                        </span>
                    ))}
                    {form.job_types.length === 0 && <span className="text-xs text-muted">No job types configured.</span>}
                </div>
                {isAdmin && (
                    <div className="flex gap-2">
                        <Input
                            value={newJobType}
                            onChange={(e) => setNewJobType(e.target.value)}
                            placeholder="e.g. Boundary survey"
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addJobType(); } }}
                        />
                        <Button onClick={addJobType} variant="outline" className="gap-2 shrink-0"><Plus size={14} /> Add</Button>
                    </div>
                )}
            </section>

            {/* Save */}
            {isAdmin && (
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label>Reason for change (audit)</Label>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What changed and why?" />
                    </div>
                    <Button
                        data-testid={ADMIN.settingsSave}
                        onClick={save} disabled={busy}
                        className="gap-2 h-11"
                    >
                        <Save size={14} /> Save settings
                    </Button>
                </div>
            )}
        </div>
    );
}
